// src/components/antenna/antenna-header.tsx
import { useMemo, useState } from "react";
import {
  Radio, FileText, FileBarChart,
  Download, ChevronDown, Loader2, RotateCw, Check, Sparkles,
} from "lucide-react";
import { ModuleHeader } from "../shell/module-header";
import { toast } from "sonner";
import { buildApiUrl } from "@/api/url-route";
import {
  AuthExpiredError,
  getStoredToken,
  notifyAuthExpired,
} from "@/api/api-fetch";
import { MODEL_CATALOG, getModelLabel } from "@/app/constants/models";
import { AntennaRunSelector } from "./antenna-run-selector";
import type { MonitoringHistoryItem } from "@/app/services/brand/monitoring-history-api";
import { Banner, Button } from "../kit";
import type { FailedModel } from "@/app/services/brand/brand-monitoring-ws";

const FAILURE_LABEL: Record<string, string> = {
  timeout:              "timed out",
  rate_limited:         "rate limited",
  spend_limit:          "spend limit reached",
  provider_error:       "provider error",
  empty_response:       "empty answer",
  unparseable_response: "unreadable answer",
  not_runnable:         "not available",
  no_result:            "no result",
};

export function failureLabel(errorType: string): string {
  return FAILURE_LABEL[errorType] ?? "failed";
}

/**
 * Admin-only notice for lanes the run planned that produced no analysis
 * (763a4e6). On main this lived in the header's "Analysis Scope" line; the V2
 * band is breadcrumb, title and description only (UPM-144), so the failures
 * render as a banner at the top of the page stack instead. Renders nothing
 * when no lane failed (the dashboard already passes [] to non-admins).
 */
export function FailedLanesNotice({
  failedModels,
  analyzedCount,
}: {
  failedModels: FailedModel[];
  analyzedCount: number;
}) {
  if (!failedModels.length) return null;
  const planned = analyzedCount + failedModels.length;
  return (
    <Banner tone="error">
      {analyzedCount} of {planned} models analyzed. Failed:{" "}
      {failedModels.map((f, i) => (
        <span key={`failed-${f.model}`} title={f.reason}>
          {i > 0 ? ", " : ""}
          {getModelLabel(f.model)} ({failureLabel(f.errorType)})
        </span>
      ))}
    </Banner>
  );
}

// ─── Inline download helper ───────────────────────────────────────────────────

async function downloadBlob(apiPath: string, filename: string): Promise<void> {
  const base  = buildApiUrl();
  const token = getStoredToken();
  if (!token) {
    notifyAuthExpired(apiPath);
    throw new AuthExpiredError(apiPath);
  }
  const res = await fetch(`${base}${apiPath}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) {
    notifyAuthExpired(apiPath);
    throw new AuthExpiredError(apiPath);
  }
  if (!res.ok) {
    // Log the server-side detail for debugging; never surface it to the client.
    try {
      const j = await res.json();
      console.error(`Export failed (${res.status})`, j?.detail ?? j);
    } catch {
      console.error(`Export failed (${res.status})`);
    }
    throw new Error("Export failed. Please try again or contact support.");
  }
  const blob = await res.blob();
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ─── Re-run analysis dropdown (model picker) ──────────────────────────────────

function RerunMenu({
  currentModels,
  onRerun,
}: {
  currentModels: string[];
  onRerun: (models: string[]) => void;
}) {
  // Brand-monitoring-eligible models: every live model that is not a
  // measurement-only variant (gptweb). Mirrors DEFAULT_LLM_MODELS — keeps the
  // measurement-only ChatGPT (Web) lane out of the client re-run picker.
  const liveModels = useMemo(
    () => MODEL_CATALOG.filter((m) => m.availability === "live" && !m.excludedFromAnalytics),
    [],
  );

  // The fallback SELECTION when a run carries no models of its own, so opt-in
  // lanes are excluded: liveModels above is the eligibility set and still
  // includes them, which is what lets a run that explicitly used one carry it
  // forward. (main, AI Mode lane. DATA logic, kept through the rebase.)
  const recommendedLive = useMemo(
    () =>
      MODEL_CATALOG.filter(
        (m) =>
          m.recommended &&
          m.availability === "live" &&
          !m.excludedFromAnalytics &&
          m.defaultSelected !== false,
      ).map((m) => String(m.id)),
    [],
  );

  // Default the selection to whatever the loaded run used (if those models are
  // still monitoring-eligible), else the recommended set. A legacy run that
  // included gptweb won't carry it forward, since it's no longer eligible.
  const initial = useMemo(() => {
    const eligible = new Set(liveModels.map((m) => String(m.id)));
    const fromCurrent = (currentModels || []).map(String).filter((m) => eligible.has(m));
    return fromCurrent.length ? fromCurrent : recommendedLive;
  }, [currentModels, liveModels, recommendedLive]);

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string[]>(initial);

  const toggle = (id: string) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleRun = () => {
    if (!selected.length) {
      toast.error("Pick at least one model to re-run.");
      return;
    }
    setOpen(false);
    onRerun(selected);
  };

  return (
    <div className="relative">
      <Button
        variant="secondary"
        onClick={() => {
          setSelected(initial);
          setOpen((v) => !v);
        }}
        className="flex items-center gap-2 shrink-0"
        title="Run brand monitoring again for this brand"
        icon={RotateCw}
      >Re-run
        <ChevronDown className="w-3 h-3 opacity-80" />
      </Button>

      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 z-20 rounded-xl border shadow-xl overflow-hidden"
            style={{
              backgroundColor: "var(--card)",
              borderColor:     "var(--border)",
              minWidth:        300,
            }}
          >
            <div
              className="px-4 py-3 flex items-center justify-between"
              style={{ borderBottom: "1px solid var(--border)" }}
            >
              <div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 700 }}>
                  Re-run analysis
                </div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  This brand only · pick models
                </div>
              </div>
              <button
                onClick={() => setSelected(recommendedLive)}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-md"
                style={{
                  backgroundColor: "var(--spectral-violet-8)",
                  color: "var(--spectral-violet)",
                  border: "1px solid var(--spectral-violet-30)",
                  fontSize: "var(--text-xs)",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <Sparkles className="w-3 h-3" />
                Recommended
              </button>
            </div>

            <div className="p-2 max-h-[260px] overflow-y-auto">
              {liveModels.map((m) => {
                const id = String(m.id);
                const on = selected.includes(id);
                return (
                  <button
                    key={id}
                    onClick={() => toggle(id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-muted"
                    style={{ background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
                  >
                    <div
                      style={{
                        width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                        display: "flex", alignItems: "center", justifyContent: "center",
                        border: `1px solid ${on ? "var(--accent)" : "var(--border)"}`,
                        backgroundColor: on ? "var(--accent)" : "transparent",
                      }}
                    >
                      {on && <Check className="w-3 h-3" style={{ color: "white" }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
                        {m.label}
                      </div>
                      <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                        {m.provider}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div
              className="px-4 py-3 flex items-center justify-between gap-3"
              style={{ borderTop: "1px solid var(--border)" }}
            >
              <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                {selected.length} selected
              </span>
              <Button
                onClick={handleRun}
                className="flex items-center gap-2"
                icon={RotateCw}
              >Run analysis
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Full Report export dropdown ──────────────────────────────────────────────

// Export Report is off until the export is redesigned (phase 15D). Kept as a
// single flag rather than deleted markup so restoring it is a one-line change
// and the component cannot rot in the meantime.
const SHOW_FULL_REPORT_EXPORT = false;

function FullReportExportMenu({
  runId,
  brandName,
}: {
  runId?: number;
  brandName: string;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  const safe     = (brandName || "brand").replace(/\s+/g, "_").slice(0, 40);
  const disabled = !runId || !!busy;

  const handle = async (key: string, fn: () => Promise<void>) => {
    if (!runId) {
      toast.error("Report isn't ready yet — please wait for the analysis to finish.");
      return;
    }
    setBusy(key);
    setOpen(false);
    try {
      await fn();
      toast.success("Export ready");
    } catch (e: any) {
      console.error("Export failed", e);
      toast.error("Export failed. Please try again or contact support.");
    } finally {
      setBusy(null);
    }
  };


  return (
    <div className="relative">
      <Button
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 shrink-0 whitespace-nowrap"
        title="Download the full brand monitoring report"
      >
        {busy
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <Download className="w-4 h-4" />}
        {busy ? "Generating…" : "Export Report"}
        <ChevronDown className="w-3 h-3 opacity-80" />
      </Button>

      {open && (
        <>
          {/* Click-outside catcher */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 mt-2 z-20 rounded-xl border shadow-xl overflow-hidden"
            style={{
              backgroundColor: "var(--card)",
              borderColor:     "var(--border)",
              minWidth:        280,
            }}
          >
            {/* Full Report — PDF (print-friendly, light theme) */}
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
              onClick={() =>
                handle(
                  "pdf",
                  () => downloadBlob(
                    `/monitoring/runs/${runId}/export/pdf`,
                    `monitoring_${safe}_${runId}.pdf`,
                  ),
                )
              }
            >
              <FileText className="w-4 h-4 flex-shrink-0" style={{ color: "var(--infrared-coral)" }} />
              <div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Full Report (.pdf)</div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  Print-friendly multi-page report
                </div>
              </div>
            </button>

            {/* Full Report — PPTX (dashboard mirror) */}
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted transition-colors"
              onClick={() =>
                handle(
                  "pptx",
                  () => downloadBlob(
                    `/monitoring/runs/${runId}/export/pptx`,
                    `monitoring_${safe}_${runId}.pptx`,
                  ),
                )
              }
            >
              <FileBarChart className="w-4 h-4 flex-shrink-0" style={{ color: "var(--accent)" }} />
              <div>
                <div style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>Full Report (.pptx)</div>
                <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                  Slide deck — dashboard mirror
                </div>
              </div>
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Header ───────────────────────────────────────────────────────────────────

interface AntennaHeaderProps {
  brandName: string;
  /** Run ID for the Export Report dropdown. Omit while the run is still loading. */
  runId?: number;
  /** Models used by the currently loaded run — seeds the re-run picker. */
  selectedModels?: string[];
  /** When provided, shows a "Re-run" model picker for this brand only. */
  onRerun?: (models: string[]) => void;
  /** Past monitoring runs for this brand (newest first) — powers the run switcher. */
  runHistory?: MonitoringHistoryItem[];
  /** Currently-viewed run id. */
  selectedRunId?: number | null;
  /** Switch to a previous run. */
  onSelectRun?: (runId: number) => void;
  /** History still loading. */
  runHistoryLoading?: boolean;
}

export function AntennaHeader({
  brandName,
  runId,
  selectedModels = [],
  onRerun,
  runHistory = [],
  selectedRunId = null,
  onSelectRun,
  runHistoryLoading = false,
}: AntennaHeaderProps) {
  // The band is breadcrumb, title and description only (UPM-144). The funnel
  // numbers that briefly lived here now render once, in the hero row on the
  // page below, which is the single funnel presentation across the product.
  return (
    <ModuleHeader
      page="Brand Monitoring"
      icon={Radio}
      title="Where you stand with AI."
      description="Your measured funnel from the latest read: primary recommendation, top 5, and mention rate, with what's driving them."
      selectorSlot={
        onSelectRun ? (
          <AntennaRunSelector
            runs={runHistory}
            selectedRunId={selectedRunId}
            onSelect={onSelectRun}
            loading={runHistoryLoading}
          />
        ) : undefined
      }
      actionsSlot={
        <div className="flex items-center gap-2 flex-wrap">
          {onRerun && (
            <RerunMenu currentModels={selectedModels} onRerun={onRerun} />
          )}
          {/* Export Report is HIDDEN pending its redesign (phase 15D). The
              component and its whole dropdown stay intact and unmodified;
              flip this flag to bring it back. The run/date selector beside it
              is unaffected. */}
          {SHOW_FULL_REPORT_EXPORT && (
            <FullReportExportMenu runId={runId} brandName={brandName} />
          )}
        </div>
      }
    />
  );
}
